<script lang="ts">
  import Button from '$lib/components/Button.svelte';
  import type { LicenseDetails } from '$lib/root/LicenseQuotingUtils';
  import GroupLicensePricingCalculator from '$lib/teach/GroupLicensePricingCalculator.svelte';
  import type {
    GroupLicensePrice,
    TestingLicenseRate,
  } from 'shared/definitions/license-definitions';
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import { twMerge } from 'tailwind-merge';
  import TestingLicensePricing from './TestingLicensePricing.svelte';

  export let selectedTabClass: string = '';
  export let unselectedTabClass: string = '';

  export let licenseType: 'group' | 'testing' = 'group';
  export let showActionButtons: boolean = false;
  export let students: number = 5;
  let testStudents: number = students;

  export let months: 6 | 12 = 12;
  export let starts: Date = new Date();
  export let emitCompletePurchaseImmediately = false;

  let testingLicensePricingIsValid = true;
  let groupLicensePricingIsValid = true;

  interface $$Events {
    quoteRequested: CustomEvent<LicenseDetails>;
    purchaseRequested: CustomEvent<LicenseDetails>;
  }

  const dispatch = createEventDispatcher<$$Events>();

  let minStudents: number = 5;
  let activeTab: 'group' | 'testing' | 'none' = licenseType;
  let groupLicensePrice: GroupLicensePrice;
  let testingLicenseRate: TestingLicenseRate;

  function dispatchPurchase() {
    dispatch('purchaseRequested', {
      // @ts-expect-error
      quantity: licenseType === 'group' ? students : testStudents,
      months: licenseType === 'group' ? months : 12,
      type: licenseType,
      starts: starts || undefined,
      expectedSubtotal:
        licenseType === 'group' ? groupLicensePrice.total : testingLicenseRate.total,
    });
  }
</script>

<!-- Tabs -->
<div role="tablist" aria-label="Pricing Calculators" class="flex w-full items-end h-16">
  <button
    role="tab"
    aria-selected={activeTab === 'group'}
    class:active={activeTab === 'group'}
    class="tab {activeTab === 'group'
      ? twMerge('bg-orange h-20', selectedTabClass)
      : twMerge('bg-orange h-14', unselectedTabClass)}"
    on:click={() => {
      licenseType = 'group';
      if (activeTab !== 'group') activeTab = 'group';
    }}
    aria-controls="group-license-pricing-calculator"
    id="group-license-tab-button">Group License</button
  >
  <button
    role="tab"
    aria-selected={activeTab === 'testing'}
    class:active={activeTab === 'testing'}
    class="tab {activeTab === 'testing'
      ? twMerge('h-20 bg-green', selectedTabClass)
      : twMerge('h-14 bg-green', unselectedTabClass)}"
    on:click={() => {
      licenseType = 'testing';
      if (activeTab !== 'testing') activeTab = 'testing';
    }}
    aria-controls="testing-only-license-pricing-calculator"
    id="testing-only-license-tab-button">Testing-Only License</button
  >
</div>
<!-- /Tabs -->

<!-- Panels -->
<div class="relative text-gray pricing-shadow bg-white pb-4">
  {#if activeTab === 'group'}
    <div
      id="group-license-pricing-calculator"
      role="tabpanel"
      aria-labelledby="group-license-tab-button"
      class=" z-0 w-full pt-4"
      in:fade={{ duration: 150 }}
      on:outroend={() => (activeTab = 'testing')}
    >
      <div class="my-4">
        <GroupLicensePricingCalculator
          bind:minStudents
          bind:students
          bind:months
          bind:starts
          bind:price={groupLicensePrice}
          bind:isValid={groupLicensePricingIsValid}
          showDates={showActionButtons}
          on:ready={() => {
            if (emitCompletePurchaseImmediately) {
              dispatchPurchase();
            }
          }}
        />
      </div>
    </div>
  {/if}
  {#if activeTab === 'testing'}
    <div
      id="group-license-pricing-calculator"
      role="tabpanel"
      aria-labelledby="testing-only-license-tab-button"
      class=" z-0"
      in:fade={{ duration: 150 }}
      on:outroend={() => (activeTab = 'group')}
    >
      <TestingLicensePricing
        showDates={showActionButtons}
        bind:students={testStudents}
        bind:starts
        bind:rate={testingLicenseRate}
        bind:isValid={testingLicensePricingIsValid}
        on:ready={() => {
          if (emitCompletePurchaseImmediately) {
            dispatchPurchase();
          }
        }}
      />
    </div>
  {/if}
  {#if showActionButtons}
    <div class="flex w-fit space-x-4 mx-auto my-8">
      <Button
        disabled={licenseType === 'group'
          ? !groupLicensePricingIsValid
          : !testingLicensePricingIsValid}
        color={licenseType === 'group' ? 'orange' : 'green'}
        on:click={() => {
          dispatch('quoteRequested', {
            // @ts-expect-error
            quantity: licenseType === 'group' ? students : testStudents,
            months: licenseType === 'group' ? months : 12,
            type: licenseType,
            starts: starts || undefined,
            expectedSubtotal:
              licenseType === 'group' ? groupLicensePrice.total : testingLicenseRate.total,
          });
        }}>Get Quote</Button
      >
      <Button
        disabled={licenseType === 'group'
          ? !groupLicensePricingIsValid
          : !testingLicensePricingIsValid}
        color={licenseType === 'group' ? 'orange' : 'green'}
        on:click={dispatchPurchase}>Purchase License</Button
      >
    </div>
  {/if}
</div>

<!-- /Panels -->
<style lang="postcss">
  .tab {
    /* font-size: 25px; */
    line-height: 44px;
    transition: 0.15s all;
    box-shadow: 1px 0 10px #00000036;
    @apply text-xl md:text-2xl font-medium text-center rounded-t-lg w-1/2 tracking-wide text-gray-50;
  }

  .pricing-shadow {
    box-shadow: 1px 4px 10px #00000036;
  }

  .tab.active {
    @apply text-white;
  }
</style>
